import {
  faCircleCheck,
  faCircleEllipsis,
  faCircleHalfStroke,
  faCircleMinus,
} from '@fortawesome/pro-solid-svg-icons';
import cloneDeep from 'lodash/cloneDeep';
import times from 'lodash/times';
import {
  CreateTrainingPlanInput,
  TrainingPlanBasicsFragment,
  TrainingPlanBlockDetailsFragment,
  TrainingPlanBlockInput,
  TrainingPlanDetailsFragment,
  TrainingPlanEquipmentCategory,
  TrainingPlanExerciseDetailsFragment,
  TrainingPlanExerciseInput,
  TrainingPlanSetDetailsFragment,
  TrainingPlanSetInput,
  TrainingPlanWeekDetailsFragment,
  TrainingPlanWeekInput,
  TrainingPlanWorkoutDetailsFragment,
  TrainingPlanWorkoutInput,
} from '../../../graphql/types';
import { BLUE200, BRIGHTBLUE200, GREEN200, ORANGE200 } from '../../../theme';
import { shortDateYear } from '../../../utils/date';

export type AttributeLabels = {
  difficulties: Record<string, string>;
  durations: Record<number, string>;
  equipmentCategories: Map<
    TrainingPlanEquipmentCategory | null | undefined,
    string
  >;
  frequencies: Record<number, string>;
  phases: Record<number, string>;
};

export function getTrainingPlanSummary(
  plan: TrainingPlanBasicsFragment,
  labels: AttributeLabels,
): string {
  return [
    plan.type ? plan.type.name : null,
    plan.difficulty ? (labels.difficulties[plan.difficulty] ?? null) : null,
    labels.equipmentCategories.get(plan.equipmentCategory) ?? null,
    `${plan.weeks.count} week${plan.weeks.count === 1 ? '' : 's'}`,
    plan.durationMinutes > 0
      ? (labels.durations[plan.durationMinutes] ?? null)
      : null,
    plan.frequency ? (labels.frequencies[plan.frequency] ?? null) : null,
    plan.phase != null ? (labels.phases[plan.phase] ?? null) : null,
  ]
    .filter(Boolean)
    .join(' • ');
}

const convertTemplateExerciseToPlanInputExercise = (
  exercise: TrainingPlanExerciseDetailsFragment,
): TrainingPlanExerciseInput => ({
  order: exercise.order,
  count: parseInt(exercise.count.toString()),
  unit: exercise.unit.value,
  exerciseId: exercise.exercise.id,
});

const convertTemplateSetToPlanInputSet = (
  set: TrainingPlanSetDetailsFragment,
): TrainingPlanSetInput => ({
  order: set.order,
  trainingPlanExercises: set.trainingPlanExercises.edges.map(
    ({ node: exercise }) =>
      convertTemplateExerciseToPlanInputExercise(exercise),
  ),
});

const convertTemplateBlockToPlanInputBlock = (
  block: TrainingPlanBlockDetailsFragment,
): TrainingPlanBlockInput => ({
  order: block.order,
  trainingComponent: block.trainingComponent.value,
  sets: times(block.sets.count, (index) =>
    convertTemplateSetToPlanInputSet({
      ...block.sets.edges[0].node,
      order: index,
    }),
  ),
});

const convertTemplateWorkoutToPlanInputWorkout = (
  workout: TrainingPlanWorkoutDetailsFragment,
): TrainingPlanWorkoutInput => ({
  id: undefined,
  order: workout.order,
  blocks: workout.blocks.edges.map(({ node: block }) =>
    convertTemplateBlockToPlanInputBlock(block),
  ),
});

const convertTemplateWeekToPlanInputWeek = (
  week: TrainingPlanWeekDetailsFragment,
): TrainingPlanWeekInput => ({
  order: week.order,
  workouts: week.workouts.edges.map(({ node: workout }) =>
    convertTemplateWorkoutToPlanInputWorkout(workout),
  ),
});

export const convertTemplateToPlanInput = (
  template: TrainingPlanDetailsFragment,
  additionalInput: Pick<
    CreateTrainingPlanInput,
    'coachId' | 'userId' | 'templateId' | 'name'
  >,
): CreateTrainingPlanInput => {
  const planInput = {
    ...additionalInput,
    difficulty: template.difficulty,
    frequency: template.frequency,
    durationMinutes: template.durationMinutes,
    phase: template.phase,
    type: template.type.value,
    equipmentCategory: template.equipmentCategory,
    id: undefined,
    __typename: undefined,
    createdAt: undefined,
    completedAt: undefined,
    archivedAt: undefined,
    weeks: template.weeks.edges.map(({ node }) =>
      convertTemplateWeekToPlanInputWeek(node),
    ),
  };
  return planInput;
};

// Strips progress so a duplicated plan starts fresh and every workout is editable.
// Only workouts and exercises store completedAt; plan, week and block values
// are derived from them by the backend, and sets have none
export const toDuplicatedPlan = (
  plan: TrainingPlanDetailsFragment,
): TrainingPlanDetailsFragment => {
  const duplicate = cloneDeep(plan);
  duplicate.completedAt = null;
  duplicate.archivedAt = null;
  for (const { node: week } of duplicate.weeks.edges) {
    for (const { node: workout } of week.workouts.edges) {
      workout.completedAt = null;
      for (const { node: block } of workout.blocks.edges) {
        for (const { node: set } of block.sets.edges) {
          for (const { node: exercise } of set.trainingPlanExercises.edges) {
            exercise.completedAt = null;
          }
        }
      }
    }
  }
  return duplicate;
};

export function secondsToHMS(total: number): {
  hours: number;
  minutes: number;
  seconds: number;
} {
  const t = Math.max(0, total);
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const s = Math.floor(t % 60);
  return { hours: h, minutes: m, seconds: s };
}

export function hmsToSeconds(
  hours: number,
  minutes: number,
  seconds: number,
): number {
  return hours * 3600 + minutes * 60 + seconds;
}

export function parseDurationInput(raw: string): number {
  const s = raw.trim();
  if (!s) {
    return 0;
  }

  const hoursMatch = s.match(/(\d+)\s*[Hh]/);
  const minutesMatch = s.match(/(\d+)\s*[Mm]/);
  const secondsMatch = s.match(/(\d+)\s*[Ss]/);

  if (!hoursMatch && !minutesMatch && !secondsMatch) {
    const n = parseInt(s, 10);
    if (Number.isNaN(n) || n < 0) {
      return 0;
    }
    return n;
  }

  const h = hoursMatch ? parseInt(hoursMatch[1], 10) : 0;
  const m = minutesMatch ? parseInt(minutesMatch[1], 10) : 0;
  let sc = secondsMatch ? parseInt(secondsMatch[1], 10) : 0;

  // Handle trailing bare number with no suffix after a labelled component (e.g. "1m30")
  if (!secondsMatch && (hoursMatch || minutesMatch)) {
    const trailing = s.match(/(\d+)\s*$/);
    if (trailing) {
      sc = parseInt(trailing[1], 10);
    }
  }

  return hmsToSeconds(h, m, sc);
}

export function formatHMS(total: number): string {
  const { hours, minutes, seconds } = secondsToHMS(total);
  const parts = [
    hours > 0 ? `${hours}h` : null,
    minutes > 0 ? `${minutes}m` : null,
    seconds > 0 || (hours === 0 && minutes === 0) ? `${seconds}s` : null,
  ].filter(Boolean);
  return parts.join(' ');
}

export const getPlanIndicator = (
  plan: TrainingPlanBasicsFragment | TrainingPlanDetailsFragment,
  isCurrentPlan: boolean,
) => {
  // default: queued case
  let indicatorIcon = faCircleEllipsis;
  let indicatorColor = ORANGE200;
  let indicatorText = 'Queued';
  let dateFormatted = shortDateYear(plan.createdAt);
  let dateText = `Published ${dateFormatted}`;

  if (plan.archivedAt) {
    indicatorIcon = faCircleMinus;
    indicatorColor = BLUE200;
    indicatorText = 'Archived';
    dateFormatted = shortDateYear(plan.archivedAt);
    dateText = `Archived ${dateFormatted}`;
  } else if (plan.completedAt) {
    indicatorIcon = faCircleCheck;
    indicatorColor = GREEN200;
    indicatorText = 'Completed';
    dateFormatted = shortDateYear(plan.completedAt);
    dateText = `Completed ${dateFormatted}`;
  } else if (isCurrentPlan) {
    indicatorColor = BRIGHTBLUE200;
    indicatorIcon = faCircleHalfStroke;
    indicatorText = 'Active';
  }

  return {
    indicatorIcon,
    indicatorColor,
    indicatorText,
    dateText,
  };
};
